import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = true;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {(() => void) | null} */
  #recheckPosition = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupScrollSync();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });

    // Scroll-sync callbacks gate visibility on #isChatActive(), but if the
    // shopper scrolls before the Inbox bundle has upgraded <shopify-chat>,
    // the bar shows and nothing re-runs that check. Hide it once the
    // element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Keeps the sticky bar and the real on-page button perfectly handed off
   * to each other during scroll, so the bar visually "becomes" the real
   * button the way the Balenciaga reference does, with no gap in either
   * scroll direction.
   *
   * Fenn customization: this intentionally does NOT use IntersectionObserver.
   * That API's callbacks are asynchronous/coalesced by the browser, which is
   * exactly what caused the gap the client reported - the bar hid a beat
   * before the real button had actually reached the screen, and reappeared
   * a beat after the real button had already scrolled away. Instead this
   * tracks scroll continuously (via requestAnimationFrame, so at most once
   * per frame) and compares the real button's live position against the
   * exact spot the sticky bar occupies: the strip `stickyBar`'s height tall
   * at the very bottom of the viewport ("the dock line"). The two states
   * swap at a single, shared crossing point per edge:
   *   - hides the bar the instant the real button's top edge reaches the
   *     dock line scrolling up from below (the button has arrived exactly
   *     where the bar was sitting - the "becomes the button" moment);
   *   - shows the bar again the instant the real button's bottom edge
   *     scrolls past the top of the viewport (the button is fully gone) -
   *     immediately, not after an extra IntersectionObserver round trip.
   * Because both directions key off the same button rect, there's never a
   * moment where neither element occupies that bottom strip.
   */
  #setupScrollSync() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    if (!this.#targetAddToCartButton) return;

    const { signal } = this.#abortController;
    let scheduled = false;

    const check = () => {
      scheduled = false;
      const targetButton = this.#targetAddToCartButton;
      if (!targetButton) return;

      // Fenn customization (13/09/2026): dockLine used to be measured from the
      // BAR's own height (window.innerHeight - bar.offsetHeight), which assumed
      // the sticky button fills the bar edge-to-edge with no padding - true for
      // the original "bar IS the button" look, but no longer true now that the
      // bar has its own padding around a smaller button (the white full-width
      // strip, per Leonardo's reference). With that padding, the bar's top edge
      // sits ABOVE the button's own top edge by one top-padding's worth - so
      // comparing against the bar's full height made the swap fire too early,
      // producing a visible jump (the real button's top would still be a few
      // pixels below where the sticky BUTTON visually was). Measuring from the
      // button's own height plus the bar's bottom padding (its distance from
      // the very bottom of the screen) gives the Y position where the sticky
      // button's top actually sits when docked, matching the real button pixel
      // for pixel at the crossing point.
      const barStyle = getComputedStyle(this.refs.stickyBar);
      const paddingBottom = parseFloat(barStyle.paddingBottom) || 0;
      const dockLine = window.innerHeight - paddingBottom - this.refs.addToCartButton.offsetHeight;
      const rect = targetButton.getBoundingClientRect();

      // Fenn customization (14/09/2026): Leonardo reported the sticky bar's
      // "Adicionar ao carrinho" text sitting in a visibly different spot than
      // the real page button's text once they swap - measuring both live
      // showed the real button (inside Horizon's subgrid product-details
      // column) renders ~20px narrower than the sticky bar's own button, so
      // centering each independently put their text at different X
      // positions on screen even though both use justify-content: center.
      // Rather than guess a fixed offset (fragile across phone widths), copy
      // the real button's actual box straight from the same rect this
      // function already reads every frame, so the sticky button is always
      // pixel-identical to whatever the real button currently measures.
      this.refs.stickyBar.style.setProperty('--fenn-real-button-left', `${rect.left}px`);
      this.refs.stickyBar.style.setProperty('--fenn-real-button-width', `${rect.width}px`);

      // Fenn customization (14/09/2026): Leonardo reported the sticky bar
      // taking too long to reappear at the bottom after the real button
      // scrolls off the top while continuing to scroll down (e.g. reading
      // the accordion below) - it used to wait until the real button was
      // 100% gone (rect.bottom <= 0). Reappearing a little early, while just
      // a sliver of the real button is still peeking in from the top, makes
      // the sticky bar feel available sooner.
      const REAPPEAR_MARGIN_PX = 40;

      // True exactly while the real button occupies the dock strip: it has
      // scrolled up to (or past) the bar's position, and hasn't scrolled
      // fully off the top of the screen yet (with the early-reappear margin
      // above).
      const buttonHasTakenOver = rect.top <= dockLine && rect.bottom > REAPPEAR_MARGIN_PX;

      if (buttonHasTakenOver) {
        if (this.#isStuck) this.#hideStickyBar();
      } else if (!this.#isStuck && !this.#isChatActive()) {
        this.#showStickyBar();
      }
    };

    this.#recheckPosition = check;

    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(check);
    };

    // Fenn customization (13/09/2026): this theme moved real scrolling from the window into
    // `.page-wrapper` (assets/fenn-rolagem.js - html/body are overflow:hidden so the page itself
    // never scrolls), so `window`'s own 'scroll' event never fires here and this whole sync used to
    // run its very first check() at load (button below the fold, so nothing hides) and then never
    // again - the bar stayed stuck forever no matter how far the shopper scrolled. Reading
    // `.page-wrapper` straight from the DOM (rather than via window.fennRolador(), whose script may
    // not have run yet at this point) fixes it without depending on script load order; window resize
    // still matters for barHeight/viewport changes.
    const scroller = document.querySelector('.page-wrapper') || window;
    scroller.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });

    // Resolve the initial state synchronously - no need to wait for the
    // first scroll/resize event - matching the server-rendered
    // data-stuck="true" on ordinary product pages.
    check();
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    // Fenn customization: refs.productImage was intentionally removed from
    // the sticky bar's markup (it now shows only the button - see
    // product-information.liquid), so the fly-to-cart-to-cart-icon
    // animation below can't run without it. That's fine - the animation is
    // optional polish - but the "added" (checkmark) state set above still
    // needs to be cleared on a timer either way, otherwise
    // [data-added='true'] permanently hides the button's text (see
    // add-to-cart-button-styles.liquid) and the bar is left blank after the
    // very first add-to-cart tap.
    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) {
      this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute('data-added');
      }, 800);
      return;
    }

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        // Fenn customization (14/09/2026): a variant morph can change the real
        // button's box (e.g. going from available to sold out changes its
        // content), so re-sync the sticky button's copied geometry right away
        // instead of waiting for the next scroll/resize event.
        this.#recheckPosition?.();

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
